/**
 * brandTypography.ts
 * Shared font resolution logic for the proposal builder.
 * Matches the logic in SetupWizard.tsx / SetupPreview.tsx so canvas,
 * preview, and PDF all use the exact same heading/body font as Brand Setup.
 *
 * Storage format (brand_settings.preview_preferences.typography):
 * {
 *   heading: { source: "built_in", builtInFont: "roboto" }
 *           | { source: "uploaded", uploadedFontId: "<uuid>", uploadedFontName: "Milestone-Script" }
 *   body:    { ... same ... }
 * }
 */

export type BuiltInFontPreference = "inter" | "roboto" | "lato" | "open-sans";

export interface TypographySelection {
  source?: "built_in" | "uploaded";
  builtInFont?: BuiltInFontPreference | null;
  uploadedFontId?: string | null;
  uploadedFontName?: string | null;
}

export interface ResolvedFont {
  /** CSS font-family string ready to pass to style={{ fontFamily }} */
  fontFamily: string;
  /** Public URL of the uploaded font file — null for built-in fonts */
  fontUrl: string | null;
}

export interface BrandTypography {
  headingFont: ResolvedFont;
  bodyFont:    ResolvedFont;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

export function coerceBuiltInFont(value: unknown): BuiltInFontPreference | null {
  return value === "inter" ||
    value === "roboto"     ||
    value === "lato"       ||
    value === "open-sans"
    ? value : null;
}

export function builtInFontFamily(font: BuiltInFontPreference): string {
  switch (font) {
    case "roboto":    return "Roboto, Inter, system-ui, sans-serif";
    case "lato":      return "Lato, Inter, system-ui, sans-serif";
    case "open-sans": return '"Open Sans", Inter, system-ui, sans-serif';
    case "inter":
    default:          return "Inter, system-ui, sans-serif";
  }
}

// ---------------------------------------------------------------------------
// Core resolvers
// ---------------------------------------------------------------------------

export function getTypographySelections(
  previewPreferences: Record<string, unknown> | null | undefined,
  fontPreferenceFallback: string | null | undefined,
): { heading: TypographySelection; body: TypographySelection } {
  const fallback = coerceBuiltInFont(fontPreferenceFallback) ?? "inter";
  const fallbackSelection: TypographySelection = { source: "built_in", builtInFont: fallback };

  const typography = isRecord(previewPreferences?.typography)
    ? (previewPreferences!.typography as Record<string, unknown>)
    : null;

  const heading = isRecord(typography?.heading)
    ? (typography!.heading as TypographySelection)
    : fallbackSelection;

  const body = isRecord(typography?.body)
    ? (typography!.body as TypographySelection)
    : fallbackSelection;

  return { heading, body };
}

export function resolveFont(
  selection: TypographySelection,
  uploadedFonts: Array<{ id: string; family_name: string | null; public_url: string | null }>,
  fallback: BuiltInFontPreference,
): ResolvedFont {
  if (selection.source === "uploaded" && selection.uploadedFontId) {
    const match = uploadedFonts.find((f) => f.id === selection.uploadedFontId);
    if (match?.family_name) {
      return {
        fontFamily: `"${match.family_name}", Inter, system-ui, sans-serif`,
        fontUrl:    match.public_url ?? null,
      };
    }
  }
  return {
    fontFamily: builtInFontFamily(coerceBuiltInFont(selection.builtInFont) ?? fallback),
    fontUrl: null,
  };
}

/**
 * Top-level helper — call this anywhere you need brand heading + body fonts.
 * Pass brandSettings and the full brandFonts array from useBrandFonts().
 */
export function resolveBrandTypography(
  brandSettings: {
    font_preference?: string | null;
    preview_preferences?: Record<string, unknown> | null;
  } | null | undefined,
  uploadedFonts: Array<{ id: string; family_name: string | null; public_url: string | null }>,
): BrandTypography {
  const fallback = coerceBuiltInFont(brandSettings?.font_preference) ?? "inter";
  const { heading, body } = getTypographySelections(
    brandSettings?.preview_preferences,
    brandSettings?.font_preference,
  );
  return {
    headingFont: resolveFont(heading, uploadedFonts, fallback),
    bodyFont:    resolveFont(body,    uploadedFonts, fallback),
  };
}

/**
 * Generates @font-face CSS blocks for any uploaded fonts.
 * Returns an empty string when both fonts are built-in.
 */
export function buildFontFaceCSS(typography: BrandTypography): string {
  const blocks: string[] = [];
  const seen = new Set<string>();

  const addBlock = (font: ResolvedFont) => {
    if (!font.fontUrl) return;
    // Extract the raw family name from the CSS string (strip quotes + fallbacks)
    const name = font.fontFamily.split(",")[0].replace(/['"]/g, "").trim();
    if (seen.has(name)) return;
    seen.add(name);
    blocks.push(`@font-face { font-family: '${name}'; src: url('${font.fontUrl}'); }`);
  };

  addBlock(typography.headingFont);
  addBlock(typography.bodyFont);
  return blocks.join("\n");
}
